import { supabase } from "../lib/supabase";

const CONFLICT_TARGET = "user_id,slot_date,column_id,event_id";

/** Format date as YYYY-MM-DD (local). */
function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

/** Split a slot key (YYYY-MM-DD-hour) into { slotDate, slotHour }. */
function parseSlotKey(key) {
  const parts = String(key).split("-");
  if (parts.length !== 4) return null;
  const slotDate = parts.slice(0, 3).join("-");
  const slotHour = Number(parts[3]);
  if (!Number.isInteger(slotHour) || slotHour < 0 || slotHour > 23) return null;
  return { slotDate, slotHour };
}

async function currentUserId() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error || !user) throw new Error("Bitte melde dich an.");
  return user.id;
}

/**
 * Load the current user's plan items for a date window, returned in the
 * `{ slots: { "YYYY-MM-DD-hour": [{ columnId, eventId }] } }` shape the UI uses.
 * @param {{ startDate?: Date, days?: number }} options
 * @returns {Promise<{ slots: Record<string, Array<{ columnId: string, eventId: string }>>}>}
 */
export async function fetchPlanRange({ startDate = new Date(), days = 14 } = {}) {
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = addDays(start, days - 1);

  const { data, error } = await supabase
    .from("plan_items")
    .select("slot_date, slot_hour, column_id, event_id")
    .gte("slot_date", toDateStr(start))
    .lte("slot_date", toDateStr(end));
  if (error) throw error;

  const slots = {};
  for (const row of data || []) {
    const key = `${row.slot_date}-${row.slot_hour}`;
    const ref = { columnId: row.column_id, eventId: row.event_id };
    (slots[key] = slots[key] || []).push(ref);
  }
  return { slots };
}

/**
 * Add one item to a slot. Duplicates (same ref on the same date) are ignored.
 * @param {{ slotDate: string, slotHour: number, columnId: string|number, eventId: string|number }} params
 */
export async function addPlanItem({ slotDate, slotHour, columnId, eventId }) {
  const userId = await currentUserId();
  const { error } = await supabase.from("plan_items").upsert(
    {
      user_id: userId,
      slot_date: slotDate,
      slot_hour: slotHour,
      column_id: String(columnId),
      event_id: String(eventId),
    },
    { onConflict: CONFLICT_TARGET, ignoreDuplicates: true }
  );
  if (error) throw error;
}

/**
 * Remove one item from a slot (RLS restricts to the caller's own rows).
 * @param {{ slotDate: string, slotHour: number, columnId: string|number, eventId: string|number }} params
 */
export async function removePlanItem({ slotDate, slotHour, columnId, eventId }) {
  const { error } = await supabase
    .from("plan_items")
    .delete()
    .eq("slot_date", slotDate)
    .eq("slot_hour", slotHour)
    .eq("column_id", String(columnId))
    .eq("event_id", String(eventId));
  if (error) throw error;
}

/**
 * One-time migration: bulk-insert a local plan's slots into Supabase. Conflicts
 * (already-migrated refs) are ignored so it is safe to retry.
 * @param {Record<string, Array<{ columnId: string, eventId: string }>>} slots
 * @returns {Promise<number>} number of rows attempted
 */
export async function migrateLocalPlan(slots) {
  const userId = await currentUserId();
  const rows = [];
  for (const [key, items] of Object.entries(slots || {})) {
    const parsed = parseSlotKey(key);
    if (!parsed) continue;
    for (const item of items || []) {
      if (item?.columnId == null || item?.eventId == null) continue;
      rows.push({
        user_id: userId,
        slot_date: parsed.slotDate,
        slot_hour: parsed.slotHour,
        column_id: String(item.columnId),
        event_id: String(item.eventId),
      });
    }
  }
  if (rows.length === 0) return 0;
  const { error } = await supabase
    .from("plan_items")
    .upsert(rows, { onConflict: CONFLICT_TARGET, ignoreDuplicates: true });
  if (error) throw error;
  return rows.length;
}
